/**
 * One notification.
 *
 * Written once because it appears twice - in the bell's panel and on the
 * notifications screen - and the two drifting apart is how you end up with a
 * row that is clickable in one place and not the other.
 *
 * Unread is carried by a dot and the title's weight rather than by a tinted
 * background. A list where half the rows are filled reads as a list of two
 * kinds of thing; a dot reads as "these ones are new".
 */

import { Avatar } from './Avatar';
import { Icon } from '@/icons/Icon';
import { cn } from '@/lib/cn';
import { ago, type Notification } from '@/lib/notifications';

/** The accent each group is drawn in. Chat is the app's own colour. */
const TONE: Record<string, string> = {
  chat: 'var(--accent-hi)',
  social: 'var(--info)',
  party: 'var(--accent-hi)',
  crew: 'var(--tier-mythic)',
  you: 'var(--good)',
  system: 'var(--text-mute)'
};

export function NotificationRow({ n, onOpen, onDismiss, compact }: {
  n: Notification;
  onOpen: (n: Notification) => void;
  /** Absent in the peek panel, where there is no room for a second control. */
  onDismiss?: (n: Notification) => void;
  compact?: boolean;
}) {
  const tone = TONE[n.group] ?? 'var(--accent-hi)';

  /* How many messages a coalesced row stands for. One is not worth saying. */
  const count = typeof n.meta?.count === 'number' ? (n.meta.count as number) : 0;

  return (
    <div
      role="button"
      tabIndex={0}
      /* For the stylesheet: a scene draws unread in its own way, and gives
         a row that has just arrived its one short entrance. */
      data-note={n.read ? 'read' : 'unread'}
      /* And this one arrives: the panel's rows are what a new notification
         appears in, so they get the one short entrance. */
      data-note-in=""
      onClick={() => onOpen(n)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(n); } }}
      className={cn(
        'group flex w-full cursor-pointer items-start gap-3 rounded-[var(--radius)] text-left transition-colors hover:bg-surface-2',
        compact ? 'px-2 py-2' : 'px-3 py-2.5'
      )}
    >
      {/* The person if there is one, the kind if there is not. */}
      <span className="relative flex-none">
        {n.actor
          ? <Avatar user={n.actor} size={compact ? 32 : 38} />
          : (
            <span
              className="grid place-items-center rounded-[var(--radius)]"
              style={{
                width: compact ? 32 : 38,
                height: compact ? 32 : 38,
                background: 'color-mix(in srgb, ' + tone + ' 14%, transparent)',
                color: tone
              }}
            >
              <Icon name={n.icon} size={compact ? 15 : 17} />
            </span>
          )}

        {/* A small badge of the kind, over a face, so a mention and a friend
            request from the same person are told apart at a glance. */}
        {n.actor ? (
          <span
            className="absolute -right-1 -bottom-1 grid h-[17px] w-[17px] place-items-center rounded-full ring-2 ring-[var(--bg-1)]"
            style={{ background: 'color-mix(in srgb, ' + tone + ' 22%, var(--bg-2))', color: tone }}
          >
            <Icon name={n.icon} size={9} />
          </span>
        ) : null}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className={cn('min-w-0 flex-1 truncate text-[13.5px]', n.read ? 'font-medium text-dim' : 'font-bold')}>
            {n.title}
          </span>
          <span className="flex-none text-[11px] text-ghost tabular-nums">{ago(n.createdAt)}</span>
        </span>

        {n.body ? (
          <span className="mt-0.5 flex items-center gap-1.5">
            <span className="min-w-0 flex-1 truncate text-[12.5px] text-mute">{n.body}</span>
            {count > 1 ? (
              <span
                className="flex-none rounded-full px-1.5 text-[10px] font-extrabold tabular-nums"
                style={{ background: 'color-mix(in srgb, ' + tone + ' 18%, transparent)', color: tone }}
              >
                {count}
              </span>
            ) : null}
          </span>
        ) : null}
      </span>

      {/* Unread dot, or the way to get rid of it. Never both - they occupy
          the same place and only one of them applies at a time. */}
      <span className="flex-none self-center">
        {onDismiss ? (
          <button
            type="button"
            aria-label="Dismiss"
            title="Dismiss"
            onClick={(e) => { e.stopPropagation(); onDismiss(n); }}
            className="grid h-6 w-6 place-items-center rounded-[var(--radius-s)] text-ghost opacity-0 transition-[opacity,color] group-hover:opacity-100 hover:text-bad"
          >
            <Icon name="close" size={12} />
          </button>
        ) : null}
        {!n.read && !onDismiss ? (
          <span className="block h-[7px] w-[7px] rounded-full" style={{ background: tone }} />
        ) : null}
      </span>

      {/* On a row that can be dismissed the dot sits on the left of the
          control, so it is not lost when the cross appears on hover. */}
      {!n.read && onDismiss ? (
        <span className="flex-none self-center">
          <span className="block h-[7px] w-[7px] rounded-full" style={{ background: tone }} />
        </span>
      ) : null}
    </div>
  );
}
